import { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { CircularProgress, Grid, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { GridValueGetterParams } from '@mui/x-data-grid';

import MasterComponent from '../../common/MasterComponent';
import CheckboxComponent from '../../components/Checkbox';
import { pageActions } from '../../store/reducers/page-actions';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { userRoleActions } from '../../store/reducers/user-role';

import { ReactComponent as VrLine } from '../../assets/icons/main/verticalLine.svg';
import {
  ALL_ACCESSES_TYPES,
  CONNECTIVITY_DASHBOARD_API_PATHS,
  DEFAULT_FEATURE_TITLES,
  DIGITAL_TWIN_API_PATHS,
  USER_ROLE_ACCESS_CONTROL_DATA,
  getFeatureLabel
} from '../../utils/user-management';


interface UserRoleAccessType {
  isUserRoleAccessLoading: boolean;
  userRoleAccess: any;
  isUpdateRoleAccessSuccess: boolean;
  loggedInUserRoleId: string;
  ontologyData: Array<any>;
  loggedInUserAccess: any;
  settingsData: any;
}

const decodeValue: any = (value: any) => {
  const decodedValue = parseInt(value, 10);
  return {
    read: (decodedValue & 1) === 1,
    create: (decodedValue & 4) === 4,
    update: (decodedValue & 2) === 2,
    delete: (decodedValue & 8) === 8,
    approver: (decodedValue & 16) === 16,
  };
};

const encodedValue = (read: any, create: any, update: any, del: any, approver: any) => {
  let encodedValue = 0;
  if (read) encodedValue |= 1;
  if (create) encodedValue |= 4;
  if (update) encodedValue |= 2;
  if (del) encodedValue |= 8;
  if (approver) encodedValue |= 16;
  return encodedValue;
};

const generatePutObject = (rows: any) => {
  const attributes: any = {};
  rows.forEach((row: any) => {
    const { object_name, read, create, update, delete: del, approver } = row;

    if (object_name === 'things/connectivity_dashboard') {
      const encodeValue = encodedValue(read, create, update, del, approver);
      const attributeAccess = [encodeValue.toString()];
      CONNECTIVITY_DASHBOARD_API_PATHS.forEach((path: string | undefined) => {
        attributes[path || ''] = attributeAccess;
      });
    } else if (object_name === 'digital_twin/twin') {
      const encodeValue = encodedValue(read, create, update, del, approver);
      const attributeAccess = [encodeValue.toString()];
      DIGITAL_TWIN_API_PATHS.forEach((path: string | undefined) => {
        attributes[path || ''] = attributeAccess;
      });
    } else {
      const encodeValue = encodedValue(read, create, update, del, approver);
      attributes[object_name.trim()] = [encodeValue.toString()];
    }
  });
  return attributes;
};

const TENANT_SETTINGS_ATTR = 'tenant/settings';

function UserRoleAccessControl({
  isUserRoleAccessLoading,
  userRoleAccess,
  isUpdateRoleAccessSuccess,
  loggedInUserRoleId,
  loggedInUserAccess,
  ontologyData,
  settingsData,
}: UserRoleAccessType) {
  const themeData: any = useTheme();
  const [rows, setRows] = useState<any>([]);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { role_id }: any = useParams();
  const [selectAll, setSelectAll] = useState(false);
  const getRoleName = get(userRoleAccess, 'name', '');
  const isLoggedUserRole = userRoleAccess.id === loggedInUserRoleId;
  const navigate = useNavigate();


  useEffect(() => {
    dispatch(userRoleActions.getRoleAccessList(role_id));

    return () => {
      dispatch(userRoleActions.resetUserRoleAccess());
    };
  }, [role_id]);

  useEffect(() => {
    if (!isEmpty(userRoleAccess) && !userRoleAccess.is_allowed) {
      navigate('/user-management');
    }
  }, [JSON.stringify(userRoleAccess)]);

  useEffect(() => {
    if (isUpdateRoleAccessSuccess) {
      dispatch(userRoleActions.getRoleAccessList(role_id));
    }
  }, [isUpdateRoleAccessSuccess]);

  useEffect(() => {
    if (!isEmpty(loggedInUserAccess) && !isEmpty(userRoleAccess)) {
      if (userRoleAccess && userRoleAccess.attributes) {
        const roleAttributes: any = userRoleAccess?.attributes;
        if (Object.keys(roleAttributes).length === 0) {
          const initialRows = USER_ROLE_ACCESS_CONTROL_DATA.map((row: any) => {
            const roleNameAttribute = roleAttributes[row.object_name.trim()];
            const accessArr: boolean[] = [];
            const itemAccess = row.access;

            const userRoleAttr = get(loggedInUserAccess, row.object_name.trim(), {});

            Object.keys(itemAccess).forEach((access: string) => {
              if (userRoleAttr[access]) {
                accessArr.push(decodeValue(roleNameAttribute)[access]);
              }
            });

            return {
              ...row.access,
              all: accessArr.length === 0 ? undefined : accessArr.every((acc: boolean) => acc),
              read:
                row.object_name === TENANT_SETTINGS_ATTR
                  ? true
                  : !userRoleAttr.read
                    ? undefined
                    : decodeValue(roleNameAttribute).read,
              create: !userRoleAttr.create ? undefined : decodeValue(roleNameAttribute).create,
              update: !userRoleAttr.update ? undefined : decodeValue(roleNameAttribute).update,
              delete: !userRoleAttr.delete ? undefined : decodeValue(roleNameAttribute).delete,
              approver: !userRoleAttr.approver ? undefined : decodeValue(roleNameAttribute).approver,
              object_name: row.object_name,
              id: row.id,
              feature: getFeatureLabel(settingsData, row),
              // all: false,
            };
          });
          // setRows(initialRows);
           setRows(applyDependencyDisabling(initialRows, loggedInUserAccess));
        } else {
          const builtRows: any = USER_ROLE_ACCESS_CONTROL_DATA.map((item: any) => {
            const roleNameAttribute = roleAttributes[item.object_name.trim()];
            const accessArr: boolean[] = [];
            const itemAccess = item.access;

            const userRoleAttr = get(loggedInUserAccess, item.object_name.trim(), {});

            Object.keys(itemAccess).forEach((access: string) => {
              if (userRoleAttr[access]) {
                accessArr.push(decodeValue(roleNameAttribute)[access]);
              }
            });

            return {
              ...item,
              feature: getFeatureLabel(settingsData, item),
              all: accessArr.length === 0 ? undefined : accessArr.every((acc: boolean) => acc),

              read:
                item.object_name === TENANT_SETTINGS_ATTR
                  ? true
                  : itemAccess.read === undefined || !userRoleAttr.read
                    ? undefined
                    : decodeValue(roleNameAttribute).read,
              create:
                itemAccess.create === undefined || !userRoleAttr.create
                  ? undefined
                  : decodeValue(roleNameAttribute).create,
              update:
                itemAccess.update === undefined || !userRoleAttr.update
                  ? undefined
                  : decodeValue(roleNameAttribute).update,
              delete:
                itemAccess.delete === undefined || !userRoleAttr.delete
                  ? undefined
                  : decodeValue(roleNameAttribute).delete,
              approver:
                itemAccess.approver === undefined || !userRoleAttr.approver
                  ? undefined
                  : decodeValue(roleNameAttribute).approver,
            };
          })
          // console.log('builtRows', builtRows);
          setRows(applyDependencyDisabling(builtRows, loggedInUserAccess));
          //  setRows(builtRows);
        }
      }
    }
  }, [loggedInUserAccess, userRoleAccess]);

  const applyDependencyDisabling = (rows: any, loggedInUserAccess: any) => {
    return rows.map((row: any) => {
      if (!row.dependency || row.object_name === 'tenant/settings') return row;

      // For each dependency, check if logged-in user has read access
      const allDepsHaveRead = row.dependency.every((depObjName: any) => {
        const depAccess = loggedInUserAccess[depObjName];
        return depAccess && depAccess.read === true;
      });

      if (!allDepsHaveRead) {
        // Disable all accesses for this row
        const newRow = { ...row };
        ALL_ACCESSES_TYPES.forEach(type => { newRow[type] = undefined; });
        newRow.all = undefined;
        return newRow;
      }
      return row;
    });
  };

  const handleCheckChange = (params: GridValueGetterParams, field: string) => {
    setRows((prevRows: any) => {
      const updatedRows: any = [...prevRows];
      const index = updatedRows.findIndex((row: any) => row.id === params.id);

      updatedRows[index][field] = !updatedRows[index][field];

      if (field === 'all') {
        const newValue = updatedRows[index][field];
        ALL_ACCESSES_TYPES.forEach((check: string) => {
          if (updatedRows[index][check] !== undefined) {
            updatedRows[index][check] = newValue;
          }
        });
      }

      if (field === 'create' || field === 'update' || field === 'delete' || field === 'approver') {
        updatedRows[index]['read'] = true;
      }

      if (field === 'read' && !updatedRows[index][field]) {
        ALL_ACCESSES_TYPES.forEach((access: string) => {
          if (updatedRows[index][access] !== undefined) {
            updatedRows[index][access] = false;
          }
        });
        // updatedRows[index]['create'] =
        //   updatedRows[index]['update'] =
        //   updatedRows[index]['delete'] =
        //   updatedRows[index]['approver'] =
        //     false;
      }

      // updatedRows[index]['all'] =
      //   updatedRows[index]['read'] &&
      //   updatedRows[index]['read'] &&
      //   updatedRows[index]['create'] &&
      //   updatedRows[index]['update'] &&
      //   updatedRows[index]['delete'] &&
      //   updatedRows[index]['approver'];

      updatedRows[index]['all'] = ALL_ACCESSES_TYPES.filter((item: string) => updatedRows[index][item] !== undefined)
        .map((item: string) => updatedRows[index][item])
        .every((item: boolean) => item);

      if (updatedRows[index]['dependency'] && updatedRows[index]['read']) {
        updatedRows[index]['dependency'].forEach((item: string) => {
          const depRowIndex = updatedRows.findIndex((row: any) => row.object_name === item);
          updatedRows[depRowIndex]['read'] = true;
        });
      }

      return updatedRows;
    });
  };

  const handleSelectAll = () => {
    setRows((prevRows: any) => {
      const updatedRows = prevRows.map((row: any) => ({
        ...row,
        all: row.all === undefined ? undefined : !selectAll,
        read: row.object_name === TENANT_SETTINGS_ATTR ? true : row.read === undefined ? undefined : !selectAll,
        create: row.create === undefined ? undefined : !selectAll,
        update: row.update === undefined ? undefined : !selectAll,
        delete: row.delete === undefined ? undefined : !selectAll,
        approver: row.approver === undefined ? undefined : !selectAll,
      }));
      setSelectAll(!selectAll);
      return updatedRows;
    });
  };

  const handleSave = (event: any) => {
    event.preventDefault();

    if (isLoggedUserRole) return;

    const attributesObj = generatePutObject(rows);
    const reqParams: any = {
      name: userRoleAccess?.name,
      id: userRoleAccess?.id,
      containerId: userRoleAccess?.containerId,
      attributes: Object.fromEntries(
        Object.entries(attributesObj).filter(
          ([, value]) => !(Array.isArray(value) && value.length === 1 && value[0] === "0")
        )
      ),
    };
    dispatch(userRoleActions.updateRoleAccessRequest(reqParams));
  };

  useEffect(() => {
    // const allValuesSelected = rows.every(
    //   (row: any) => row.read && row.create && row.update && row.delete && row.approver
    // );
    // setSelectAll(allValuesSelected);
    if (!isEmpty(rows)) {
      const selected: boolean[] = [];
      rows.forEach((row: any) => {
        ALL_ACCESSES_TYPES.forEach((access: string) => {
          if (row[access] !== undefined) {
            selected.push(row[access]);
          }
        });
      });
      setSelectAll(selected.every((item: boolean) => item === true));
    }
  }, [rows]);

  const columns = [
    {
      field: 'feature',
      headerName: 'Features',
      flex: 1,
      minWidth: 380,
      maxWidth: 380,
      renderCell: (params: any) => {
        const featureName =
          params.value !== 'Shop Floor Configuration'
            ? params.value
            : (ontologyData?.slice(-1)?.[0]?.level_name ?? 'Shop floor') + ' Configuration';

        return (
          <Tooltip title={featureName} placement='left' arrow>
            <Typography
              component='p'
              variant='subtitle1'
              sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              {featureName}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'all',
      headerName: 'All',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <CheckboxComponent
            disabled={params.row.all === undefined}
            checked={params.row.all}
            handleCheckboxChange={(event: any) => handleCheckChange(params, 'all')}
          />
        );
      },
    },
    {
      field: 'read',
      headerName: 'READ',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <CheckboxComponent
            disabled={params.row.read === undefined}
            checked={params.row.read}
            handleCheckboxChange={(event: any) => handleCheckChange(params, 'read')}
          />
        );
      },
    },
    {
      field: 'create',
      headerName: 'CREATE',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <CheckboxComponent
            disabled={params.row.create === undefined}
            checked={params.row.create}
            handleCheckboxChange={(event: any) => handleCheckChange(params, 'create')}
          />
        );
      },
    },
    {
      field: 'update',
      headerName: 'UPDATE',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <CheckboxComponent
            disabled={params.row.update === undefined}
            checked={params.row.update}
            handleCheckboxChange={(event: any) => handleCheckChange(params, 'update')}
          />
        );
      },
    },
    {
      field: 'delete',
      headerName: 'DELETE',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <CheckboxComponent
            disabled={params.row.delete === undefined}
            checked={params.row.delete}
            handleCheckboxChange={(event: any) => handleCheckChange(params, 'delete')}
          />
        );
      },
    },
    {
      field: 'approver',
      headerName: 'APPROVE',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <CheckboxComponent
            disabled={params.row.approver === undefined}
            checked={params.row.approver}
            handleCheckboxChange={(event: any) => handleCheckChange(params, 'approver')}
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (pathname.length) {
      const pathnamesTemp: any = pathname?.split('/');
      if (pathnamesTemp.length) {
        const pathnamesData: any = pathnamesTemp
          .filter((i: any, index: number) => index > 0 && index !== 2)
          .map((item: any, index: number) => {
            if (index == 0) {
              return {
                route: pathnamesTemp[0] + '/' + pathnamesTemp[1],
                pathName: item.replace('-', ' '),
                navigate: true,
              };
            } else if (index == 1) {
              return { route: '', pathName: item.replace(/-/g, ' '), navigate: false };
            } else {
              return { route: '', pathName: item.replace('-', ' '), navigate: false };
            }
          });
        dispatch(pageActions.setPagePath(pathnamesData));
        return () => {
          const data: any = [];
          dispatch(pageActions.setPagePath(data));
        };
      }
    }
  }, [userRoleAccess, pathname]);

  const partLabel = (
    <Grid container direction='row' justifyContent='space-between' alignItems='center'>
      <Grid item>
        <Typography variant='subtitle1' sx={{ paddingRight: '32px' }}>
          User Role : <b>{getRoleName}</b>
        </Typography>
      </Grid>
      <VrLine style={{ paddingRight: '32px', height: '42px !important', color: `${themeData.palette.customBorderColor.main}` }} />
      <Grid item display='flex' flexDirection='row' alignItems='center'>
        <CheckboxComponent checked={selectAll} handleCheckboxChange={handleSelectAll} />
        <Typography variant='subtitle1'>Select All</Typography>
      </Grid>
    </Grid>
  );

  const filteredRows = rows.filter((row: any) => {
    if (row.object_name === TENANT_SETTINGS_ATTR) {
      const loggedUserTenantSettings = get(loggedInUserAccess, TENANT_SETTINGS_ATTR, {});
      return (
        loggedUserTenantSettings.read &&
        loggedUserTenantSettings.create &&
        loggedUserTenantSettings.delete &&
        loggedUserTenantSettings.update
      );
    }
    return true;
  })

  return (
    <MasterComponent
      heading={!getRoleName ? <CircularProgress size={30} /> : partLabel}
      showSaveButton
      handleSave={handleSave}
      disableSaveButton={isLoggedUserRole}
      disableSaveButtonTooltipText="You can't modify your own role!"
      isLoading={isUserRoleAccessLoading}
      rows={filteredRows}
      columns={columns}
      pageSize={filteredRows.length || 20}
      isFromRoleAccess={true}
      hideFooter
      noRowsOverlay={() => (
        <Stack height='100%' alignItems='center' justifyContent='center'>
          data not available
        </Stack>
      )}
    />
  );
}

const mapStateToProps = (state: any) => ({
  isUserRoleAccessLoading: state.userRole.isUserRoleAccessLoading,
  userRoleAccess: state.userRole.userRoleAccess,
  isUpdateRoleAccessSuccess: state.userRole.isUpdateRoleAccessSuccess,
  loggedInUserRoleId: state.profile.userRole.id,
  loggedInUserAccess: state.profile.userAccess,
  ontologyData: state.ontologyMaster.ontologyData,
  settingsData: state.settings.settingsData,
});

export default connect(mapStateToProps)(UserRoleAccessControl);
